import React, { useState, useEffect } from 'react';
import { Handle, Position } from 'reactflow';
import { Minus, Plus, X, Paperclip, Download, FileText, FileImage, File, Save } from 'lucide-react';
import NodeTags from '../NodeTags';
import { downloadFile, saveNodeAsFile } from '../../api/filesApi';

const BaseNode = ({ data, onChange = () => {}, onSubmit, children, nodeType }) => {
  const [content, setContent] = useState(data.content || '');
  const [saveState, setSaveState] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'

  useEffect(() => {
    setContent(data.content || '');
    const errorHandler = (event) => {
      if (event.message && event.message.includes('ResizeObserver')) {
        event.stopPropagation();
        event.preventDefault();
      }
    };

    window.addEventListener('error', errorHandler);
    window.addEventListener('unhandledrejection', errorHandler);

    return () => {
      window.removeEventListener('error', errorHandler);
      window.removeEventListener('unhandledrejection', errorHandler);
    };
  }, [data.content]);

  const handleContentChange = (e) => {
    const updatedContent = e.target.value;
    setContent(updatedContent);
    if (onChange) {
      onChange({ ...data, content: updatedContent });
    }
  };

  if (data.collapsed) {
    return (
      <div className="collapsed-node">
        <Handle type="target" position={Position.Top} style={{ backgroundColor: data.handleColor }} />
        <div className="node-controls">
          <button
            className="expand-btn"
            onClick={(e) => {
              e.stopPropagation();
              data.onExpand && data.onExpand();
            }}
          >
            <Plus size={12} />
          </button>
          <button
            className="delete-btn"
            onClick={(e) => {
              e.stopPropagation();
              if (
                window.confirm(
                  'Delete this node and all of its child nodes?'
                )
              ) {
                data.onDelete && data.onDelete();
              }
            }}
          >
            <X size={12} />
          </button>
        </div>
        <Handle type="source" position={Position.Bottom} style={{ backgroundColor: data.handleColor }} />
      </div>
    );
  }

  // isPrompt=true → instruction sidecar node (violet/solid, not editable)
  // isInput=true → user-authored input node (yellow/dashed)
  // readOnly=true → system-generated output node (white/solid)
  const nodeRoleClass = data.isPrompt ? 'prompt-node' : data.isInput ? 'input-node' : (data.readOnly || data.isOutput) ? 'output-node' : '';

  return (
    <div className={`base-node ${nodeRoleClass} ${data.readOnly ? 'read-only' : ''} ${data.isLoading ? 'loading' : ''}`}>
      <div className="node-controls">
        <button className="collapse-btn" onClick={(e) => { e.stopPropagation(); data.onCollapse && data.onCollapse(); }}>
          <Minus size={12} />
        </button>
        {!data.isInput && !data.isPrompt && (nodeType === 'textNode' || nodeType === 'imageNode') && (
          <button
            className={`save-file-btn ${saveState}`}
            title={saveState === 'saved' ? 'Saved to files!' : saveState === 'error' ? 'Save failed' : 'Save to files'}
            onClick={async (e) => {
              e.stopPropagation();
              if (saveState === 'saving') return;
              setSaveState('saving');
              try {
                await saveNodeAsFile(data, nodeType);
                setSaveState('saved');
                setTimeout(() => setSaveState('idle'), 2500);
              } catch (err) {
                console.error('Save to files failed:', err);
                setSaveState('error');
                setTimeout(() => setSaveState('idle'), 2500);
              }
            }}
          >
            {saveState === 'saving' ? (
              <span style={{ fontSize: 10 }}>…</span>
            ) : saveState === 'saved' ? (
              <span style={{ fontSize: 10 }}>✓</span>
            ) : saveState === 'error' ? (
              <span style={{ fontSize: 10 }}>!</span>
            ) : (
              <Save size={12} />
            )}
          </button>
        )}
        <button
          className="delete-btn"
          onClick={(e) => {
            e.stopPropagation();
            if (
              window.confirm('Delete this node and all of its child nodes?')
            ) {
              data.onDelete && data.onDelete();
            }
          }}
        >
          <X size={12} />
        </button>
      </div>
      {data.tags && <NodeTags tags={data.tags} />}
      <Handle type="target" position={Position.Top} style={{ backgroundColor: data.handleColor }} />

      {/* Attached Files Display */}
      {data.attachedFiles && data.attachedFiles.length > 0 && (
        <div className="attached-files" style={{
          padding: '8px',
          backgroundColor: '#f0f9ff',
          borderRadius: '4px',
          marginBottom: '8px',
          fontSize: '12px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '4px', color: '#0369a1', fontWeight: 500 }}>
            <Paperclip size={12} style={{ marginRight: '4px' }} />
            <span>{data.attachedFiles.length} file{data.attachedFiles.length > 1 ? 's' : ''} attached</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {data.attachedFiles.map((file, index) => {
              const getFileIcon = () => {
                if (file.mime_type?.startsWith('image/')) return <FileImage size={12} />;
                if (file.mime_type?.startsWith('text/')) return <FileText size={12} />;
                return <File size={12} />;
              };

              return (
                <div
                  key={file.file_id || index}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '4px 6px',
                    backgroundColor: 'white',
                    borderRadius: '3px',
                    border: '1px solid #bae6fd'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flex: 1, minWidth: 0 }}>
                    {getFileIcon()}
                    <span style={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      color: '#0c4a6e',
                      fontSize: '11px'
                    }}>
                      {file.filename}
                    </span>
                  </div>
                  <button
                    onClick={async (e) => {
                      e.stopPropagation();
                      try {
                        await downloadFile(file.file_id, file.filename);
                      } catch (error) {
                        console.error('Download failed:', error);
                      }
                    }}
                    style={{
                      padding: '2px 4px',
                      backgroundColor: '#0369a1',
                      color: 'white',
                      border: 'none',
                      borderRadius: '3px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      fontSize: '10px'
                    }}
                    title="Download file"
                  >
                    <Download size={10} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="node-content">
        {children}
      </div>
      <Handle type="source" position={Position.Bottom} style={{ backgroundColor: data.handleColor }} />
      {data.isLoading && (
        <div className="loading-overlay">
          <div className="loading-spinner" />
        </div>
      )}
    </div>
  );
};

export default BaseNode;
